import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import usePageTitle from "../hooks/usePageTitle";
import axios from "axios";
import { format } from "date-fns";
import { Scanner } from "@yudiel/react-qr-scanner";
import {
  CheckCircle,
  XCircle,
  Camera,
  User,
  Calendar,
  CreditCard,
  RotateCcw,
  Scan,
  Loader2,
  Store,
  Link2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAIRED_KEY = "advanta_paired_venue";

const ScannerPage = () => {
  usePageTitle('Membership Scanner', 'Verify Advanta Plus membership cards');
  const { token } = useParams();
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [error, setError] = useState(null);
  const [showInstallTip, setShowInstallTip] = useState(true);
  const [pairedVenue, setPairedVenue] = useState(() => {
    try { return JSON.parse(localStorage.getItem(PAIRED_KEY) || "null"); } catch { return null; }
  });
  const [pairing, setPairing] = useState(false);
  const [pairError, setPairError] = useState(null);

  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

  // If we landed on /scan/pair/:token, exchange the token for the venue identity and persist it.
  useEffect(() => {
    if (!token) return;
    setPairing(true);
    setPairError(null);
    axios.get(`${API}/scanner/pair/${token}`)
      .then((res) => {
        const venue = { ...res.data, token, paired_at: new Date().toISOString() };
        localStorage.setItem(PAIRED_KEY, JSON.stringify(venue));
        setPairedVenue(venue);
        navigate("/scan", { replace: true });
      })
      .catch((err) => {
        setPairError(err.response?.data?.detail || "Pairing link not recognised. Ask the admin for a fresh link.");
      })
      .finally(() => setPairing(false));
  }, [token, navigate]);

  const unpair = () => {
    localStorage.removeItem(PAIRED_KEY);
    setPairedVenue(null);
    setResult(null);
  };

  const validateQRCode = async (qrData) => {
    setLoading(true);
    setError(null);
    setScanning(false);
    try {
      const response = await axios.post(`${API}/membership/validate`, {
        qr_data: qrData,
        business_id: pairedVenue?.business_id || null,
      });
      setResult(response.data);
    } catch (error) {
      setResult({
        valid: false,
        message: error.response?.data?.detail || error.response?.data?.message || "Validation failed"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleScan = (detectedCodes) => {
    if (detectedCodes && detectedCodes.length > 0) {
      const code = detectedCodes[0];
      if (code.rawValue) {
        console.log("QR Code detected:", code.rawValue);
        validateQRCode(code.rawValue);
      }
    }
  };

  const handleError = (err) => {
    console.error("Scanner error:", err);
    if (err?.message?.includes("Permission")) {
      setError("Camera access denied. Please allow camera permission in your browser settings.");
    } else if (err?.message?.includes("not found")) {
      setError("No camera found on this device.");
    } else {
      setError("Failed to access camera. Please try again.");
    }
    setScanning(false);
  };

  const handleManualValidation = async () => {
    if (!manualCode.trim()) return;
    setLoading(true);
    await validateQRCode(`ADVANTA:${manualCode}`);
  };

  const resetScanner = () => {
    setResult(null);
    setManualCode("");
    setError(null);
    setScanning(false);
  };

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Navigation */}
      <nav className="bg-[#0B0F19] border-b border-[#D4AF37]/10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#B8860B] flex items-center justify-center shadow-lg">
              <span className="text-[#020617] font-bold text-lg tracking-tight">A+</span>
            </div>
            <span className="font-['Playfair_Display'] text-2xl text-white">Advanta <span className="text-[#D4AF37]">Plus</span></span>
          </Link>
          <span className="text-[#D4AF37] text-sm font-medium">Business Scanner</span>
        </div>
      </nav>

      <div className="max-w-lg mx-auto px-6 py-8">
        {/* Pairing in progress */}
        {pairing && (
          <div className="bg-[#0B0F19] rounded-2xl p-8 border border-[#D4AF37]/10 text-center mb-6">
            <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin mx-auto mb-3" />
            <p className="text-white/70 text-sm">Pairing this device to the venue…</p>
          </div>
        )}

        {/* Not paired yet — show pairing instructions */}
        {!pairedVenue && !pairing && (
          <div className="bg-[#0B0F19] rounded-2xl p-6 border border-[#D4AF37]/10 mb-6">
            <div className="text-center mb-4">
              <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-3">
                <Link2 className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h2 className="font-['Playfair_Display'] text-xl text-white mb-1">Pair this device to a venue</h2>
              <p className="text-white/50 text-sm">
                Open the pairing link your Advanta admin sent you on this phone, just once. The device will stay linked to your venue so every scan attributes to your business.
              </p>
            </div>
            {pairError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mt-3 text-red-300 text-xs text-center">
                {pairError}
              </div>
            )}
          </div>
        )}

        {/* Paired — show venue banner */}
        {pairedVenue && (
          <div className="bg-[#047857]/10 border border-[#047857]/30 rounded-xl p-3 mb-6 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#047857]/20 flex items-center justify-center flex-shrink-0">
              <Store className="w-5 h-5 text-[#047857]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white/50 text-[10px] uppercase tracking-wider">Scanning for</p>
              <p className="text-white text-sm font-medium truncate">{pairedVenue.business_name}</p>
            </div>
            <button
              onClick={unpair}
              className="text-white/40 hover:text-white/70 text-xs underline"
              data-testid="unpair-btn"
            >
              unpair
            </button>
          </div>
        )}

        {/* Add to Home Screen tip */}
        {showInstallTip && !isStandalone && pairedVenue && (
          <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-4 mb-6 text-center">
            <p className="text-white text-sm font-medium mb-1">Save this scanner to your phone</p>
            <p className="text-white/60 text-xs mb-3">
              Tap the share button <span className="inline-block">&#x2191;</span> then "Add to Home Screen" to access instantly
            </p>
            <button onClick={() => setShowInstallTip(false)} className="text-[#D4AF37] text-xs underline">Dismiss</button>
          </div>
        )}

        {/* Header */}
        {pairedVenue && (
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-3">
              <Scan className="w-7 h-7 text-[#D4AF37]" />
            </div>
            <h1 className="font-['Playfair_Display'] text-2xl text-white mb-1">
              Validate Membership
            </h1>
            <p className="text-white/50 text-sm">
              Scan customer's QR code to verify
            </p>
          </div>
        )}

        {/* Error Display */}
        {pairedVenue && error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4">
            <div className="flex items-start gap-3">
              <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-400 font-medium text-sm">Camera Error</p>
                <p className="text-red-400/70 text-xs mt-1">{error}</p>
              </div>
            </div>
            <Button 
              onClick={() => { setError(null); setScanning(true); }}
              className="btn-gold-outline w-full mt-3 py-2 text-sm rounded-lg"
            >
              Try Again
            </Button>
          </div>
        )}

        {/* Scanner Area */}
        {pairedVenue && !result && !loading && !error && (
          <div className="bg-[#0B0F19] rounded-2xl p-5 border border-[#D4AF37]/10">
            {!scanning ? (
              <div className="text-center">
                <Button 
                  onClick={() => setScanning(true)}
                  className="btn-gold px-8 py-5 text-base rounded-xl mb-5 w-full"
                  data-testid="start-scanner-btn"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  Open Camera
                </Button>
                
                <div className="relative my-5">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/10"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-[#0B0F19] px-4 text-white/40 text-xs">or enter code</span>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    placeholder="Verification code..."
                    className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/30 focus:border-[#D4AF37] focus:outline-none text-sm"
                    data-testid="manual-code-input"
                    onKeyPress={(e) => e.key === 'Enter' && handleManualValidation()}
                  />
                  <Button 
                    onClick={handleManualValidation}
                    className="btn-gold px-5 rounded-lg"
                    data-testid="validate-manual-btn"
                  >
                    Go
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <div className="relative bg-black rounded-xl overflow-hidden" style={{ aspectRatio: '4/3' }}>
                  <Scanner
                    onScan={handleScan}
                    onError={handleError}
                    constraints={{
                      facingMode: "environment"
                    }}
                    formats={["qr_code"]}
                    components={{
                      audio: false,
                      torch: true
                    }}
                    styles={{
                      container: {
                        width: "100%",
                        height: "100%"
                      },
                      video: {
                        width: "100%",
                        height: "100%",
                        objectFit: "cover"
                      }
                    }}
                  />
                  
                  {/* Scanning overlay */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-48 h-48 border-2 border-[#D4AF37] rounded-lg relative">
                      <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-[#D4AF37] rounded-tl-lg"></div>
                      <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-[#D4AF37] rounded-tr-lg"></div>
                      <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-[#D4AF37] rounded-bl-lg"></div>
                      <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-[#D4AF37] rounded-br-lg"></div>
                    </div>
                  </div>
                </div>
                
                <p className="text-white/50 text-xs text-center mt-3">
                  Position QR code within the frame
                </p>
                
                <Button 
                  onClick={() => setScanning(false)}
                  className="btn-gold-outline w-full mt-3 py-3 rounded-lg text-sm"
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Loading */}
        {pairedVenue && loading && (
          <div className="bg-[#0B0F19] rounded-2xl p-10 border border-[#D4AF37]/10 text-center">
            <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin mx-auto mb-3" />
            <p className="text-white/70 text-sm">Validating...</p>
          </div>
        )}

        {/* Result */}
        {pairedVenue && result && !loading && (
          <div className={`rounded-2xl p-6 border ${result.valid ? 'bg-[#047857]/10 border-[#047857]/30' : 'bg-red-500/10 border-red-500/30'}`} data-testid="validation-result">
            <div className="text-center mb-5">
              {result.valid ? (
                <div className="w-16 h-16 rounded-full bg-[#047857]/20 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-8 h-8 text-[#047857]" />
                </div>
              ) : (
                <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-3">
                  <XCircle className="w-8 h-8 text-red-400" />
                </div>
              )}
              
              <h2 className={`font-['Playfair_Display'] text-xl ${result.valid ? 'text-[#047857]' : 'text-red-400'}`}>
                {result.valid ? 'Valid Membership' : 'Invalid'}
              </h2>
              
              {!result.valid && (
                <p className="text-red-400/70 text-sm mt-2">{result.message}</p>
              )}
            </div>

            {result.valid && result.user && (
              <div className="space-y-3">
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                      <User className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-white font-medium" data-testid="member-name">
                        {result.user.first_name} {result.user.last_name}
                      </p>
                      <p className="text-white/50 text-xs">{result.user.email}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/5 rounded-xl p-3">
                    <div className="flex items-center gap-1 mb-1">
                      <CreditCard className="w-3 h-3 text-[#D4AF37]" />
                      <span className="text-white/50 text-xs">Plan</span>
                    </div>
                    <p className="text-white text-sm font-medium" data-testid="member-plan">
                      {result.membership.plan_name}
                    </p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3">
                    <div className="flex items-center gap-1 mb-1">
                      <Calendar className="w-3 h-3 text-[#D4AF37]" />
                      <span className="text-white/50 text-xs">Expires</span>
                    </div>
                    <p className="text-white text-sm font-medium" data-testid="member-expiry">
                      {format(new Date(result.membership.expiry_date), "MMM d, yyyy")}
                    </p>
                  </div>
                </div>

                {result.source && (
                  <div className="text-center pt-2">
                    <span className={`text-xs px-2 py-1 rounded ${result.source === 'wallet_pass' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'}`}>
                      {result.source === 'wallet_pass' ? 'Wallet Pass' : 'Live App'}
                    </span>
                  </div>
                )}

                {result.verified_at && (
                  <div className="text-center pt-3 border-t border-white/10">
                    <p className="text-white/40 text-xs">
                      Verified {format(new Date(result.verified_at), "HH:mm:ss")}
                    </p>
                  </div>
                )}
              </div>
            )}

            <Button 
              onClick={resetScanner}
              className="btn-gold w-full mt-5 py-4 rounded-xl"
              data-testid="scan-again-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Scan Another
            </Button>
          </div>
        )}

        {/* Quick Instructions */}
        {pairedVenue && (
          <div className="mt-6 text-center">
            <p className="text-white/40 text-xs">
              Scan QR code from customer's app or wallet pass
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScannerPage;
